import { useEffect, useState, useMemo } from "react";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
} from "chart.js";
import { Pie, Line } from "react-chartjs-2";
import ReactMarkdown from "react-markdown";
import { useTranslation } from "react-i18next";
import useRetryFetch from "../../hooks/useRetryFetch";
import { config } from "../../Environment";
import { GeneratingLoader } from "../ui/generating-loader";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { normalizeDataEventAnalysisItems } from "../../lib/dataEventAnalysisViz";
import {
  buildDataEventComponentIndex,
  formatDataEventCellDisplay,
  getCheckboxOptionLabel,
  getDataEventFieldLabel,
  getNumericFieldAxisBounds,
} from "../../lib/dataEventAnalysisLabels";

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
);

const LINE_COLORS = [
  { border: "rgb(59, 130, 246)", background: "rgba(59, 130, 246, 0.2)" },
  { border: "rgb(16, 185, 129)", background: "rgba(16, 185, 129, 0.2)" },
  { border: "rgb(245, 158, 11)", background: "rgba(245, 158, 11, 0.2)" },
  { border: "rgb(239, 68, 68)", background: "rgba(239, 68, 68, 0.2)" },
  { border: "rgb(139, 92, 246)", background: "rgba(139, 92, 246, 0.2)" },
  { border: "rgb(236, 72, 153)", background: "rgba(236, 72, 153, 0.2)" },
];

const PIE_BG = [
  "rgba(59, 130, 246, 0.7)",
  "rgba(16, 185, 129, 0.7)",
  "rgba(245, 158, 11, 0.7)",
  "rgba(239, 68, 68, 0.7)",
  "rgba(139, 92, 246, 0.7)",
  "rgba(236, 72, 153, 0.7)",
];

function normalizeContent(content) {
  if (!content || typeof content !== "object") {
    return {
      title: "",
      sourceActivityId: "",
      sourceDataEventKey: "",
      analysisItems: [],
      minSamples: null,
      showVizType: false,
    };
  }
  return {
    title: content.title ?? "",
    sourceActivityId: content.sourceActivityId ?? "",
    sourceDataEventKey: content.sourceDataEventKey ?? "",
    analysisItems: normalizeDataEventAnalysisItems(content),
    minSamples:
      content.minSamples != null && content.minSamples !== ""
        ? Number(content.minSamples)
        : null,
    showVizType: content.showVizType === true,
  };
}

function defaultMinSamplesForType(vizType, explicit) {
  if (explicit != null && Number.isFinite(explicit) && explicit >= 0) {
    return explicit;
  }
  if (vizType === "timeseries") return 2;
  if (vizType === "ai") return 3;
  return 1;
}

function formatWhen(d) {
  if (!d) return "";
  try {
    return new Date(d).toLocaleString();
  } catch {
    return String(d);
  }
}

/** Compact axis label to prevent crowding on dense time series. */
function formatWhenShort(d) {
  if (!d) return "";
  try {
    const dt = new Date(d);
    return dt.toLocaleTimeString([], {
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return String(d);
  }
}

/** Short date for sparse day-boundary ticks (e.g. "Apr 7"). */
function formatDateShort(d) {
  if (!d) return "";
  try {
    return new Date(d).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
    });
  } catch {
    return String(d);
  }
}

function seriesSpansMultipleDays(meta) {
  if (!Array.isArray(meta) || meta.length < 2) return false;
  try {
    const a = new Date(meta[0].t).toDateString();
    const b = new Date(meta[meta.length - 1].t).toDateString();
    return a !== b;
  } catch {
    return false;
  }
}

/** Time on most ticks; short date only at calendar day boundaries when range spans days. */
function formatTimeseriesAxisTick(meta, dataIndex) {
  if (!Array.isArray(meta) || typeof dataIndex !== "number" || !meta[dataIndex])
    return "";
  const multiDay = seriesSpansMultipleDays(meta);
  let cur;
  let prev;
  try {
    cur = new Date(meta[dataIndex].t);
    prev =
      dataIndex > 0 && meta[dataIndex - 1]
        ? new Date(meta[dataIndex - 1].t)
        : null;
  } catch {
    return "";
  }
  const dayChanged =
    !prev || cur.toDateString() !== prev.toDateString();
  if (multiDay && dayChanged) return formatDateShort(meta[dataIndex].t);
  return formatWhenShort(meta[dataIndex].t);
}

function normalizeMarkdownText(text) {
  if (typeof text !== "string") return "";
  let out = text.trim();
  if (out.startsWith("```")) {
    out = out.replace(/^```(?:markdown|md)?\s*\n/i, "");
  }
  if (out.endsWith("```")) {
    out = out.replace(/\n```\s*$/i, "");
  }
  return out;
}

function DataEventAnalysisItemBlock({
  fieldKey,
  title,
  viz,
  colorIndex,
  sortedEvents,
  minN,
  processId,
  activity,
  t,
  retryFetch,
  componentIndex,
  showVizType,
  timeseriesUseFieldScale,
}) {
  const fieldTitle = getDataEventFieldLabel(fieldKey, componentIndex);
  const displayTitle =
    typeof title === "string" && title.trim() ? title.trim() : fieldTitle;
  const fieldType = componentIndex?.get(String(fieldKey))?.type;
  const linePalette = LINE_COLORS[colorIndex % LINE_COLORS.length];

  const usableCount = useMemo(() => {
    if (!fieldKey) return 0;
    return sortedEvents.filter((ev) => {
      const v = ev.eventData?.[fieldKey];
      if (v === undefined || v === null || v === "") return false;
      if (typeof v === "object" && !Array.isArray(v)) {
        return Object.keys(v).length > 0;
      }
      return true;
    }).length;
  }, [sortedEvents, fieldKey]);

  const pieConfig = useMemo(() => {
    const counts = {};
    for (const ev of sortedEvents) {
      const raw = ev.eventData?.[fieldKey];
      if (raw && typeof raw === "object" && !Array.isArray(raw)) {
        Object.entries(raw).forEach(([optKey, on]) => {
          if (on === true) {
            counts[optKey] = (counts[optKey] || 0) + 1;
          }
        });
      }
    }
    const keys = Object.keys(counts);
    const labels = keys.map((k) =>
      getCheckboxOptionLabel(fieldKey, k, componentIndex),
    );
    const dataVals = keys.map((k) => counts[k]);
    return {
      labels,
      datasets: [
        {
          data: dataVals,
          backgroundColor: PIE_BG,
        },
      ],
    };
  }, [sortedEvents, fieldKey, componentIndex]);

  const lineConfig = useMemo(() => {
    const points = [];
    for (const ev of sortedEvents) {
      const raw = ev.eventData?.[fieldKey];
      const num =
        typeof raw === "number"
          ? raw
          : typeof raw === "string" && raw.trim() !== ""
            ? Number(raw)
            : NaN;
      if (!Number.isFinite(num)) continue;
      points.push({
        y: num,
        t: ev.created,
      });
    }
    return {
      // Stable category keys; display text comes from ticks.callback (sparse dates).
      labels: points.map((_, i) => String(i)),
      datasets: [
        {
          label: fieldTitle,
          data: points.map((p) => p.y),
          borderColor: linePalette.border,
          backgroundColor: linePalette.background,
          tension: 0.2,
        },
      ],
      meta: points,
    };
  }, [
    sortedEvents,
    fieldKey,
    fieldTitle,
    linePalette.border,
    linePalette.background,
  ]);

  /** Slider (and similar) Y range from component definition — not from logged values. */
  const timeseriesAxisBounds = useMemo(() => {
    if (viz !== "timeseries") return null;
    return getNumericFieldAxisBounds(fieldKey, componentIndex);
  }, [viz, fieldKey, componentIndex]);

  const wantsAi = viz === "ai";
  const aiAllowed =
    wantsAi && ["textbox", "textarea", "text", "orderedlist"].includes(fieldType);
  const [aiSummary, setAiSummary] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function runAi() {
      if (
        !processId ||
        !activity ||
        !aiAllowed ||
        !fieldKey ||
        usableCount < minN
      ) {
        setAiSummary("");
        setAiError(null);
        return;
      }
      setAiLoading(true);
      setAiError(null);
      try {
        const res = await retryFetch(
          `${config.API_BASE}/api/process/${processId}/data-events/summarize`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              activity,
              fieldKey,
              eventsCap: 100,
            }),
          },
        );
        if (!res.ok) {
          const errBody = await res.json().catch(() => ({}));
          throw new Error(errBody.error || `HTTP ${res.status}`);
        }
        const data = await res.json();
        if (!cancelled) {
          setAiSummary(data.summary || "");
        }
      } catch (e) {
        if (!cancelled) {
          setAiError(e.message || "error");
          setAiSummary("");
        }
      } finally {
        if (!cancelled) setAiLoading(false);
      }
    }
    runAi();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- useRetryFetch returns a new function each render
  }, [processId, activity, aiAllowed, fieldKey, usableCount, minN]);

  const showEmpty =
    sortedEvents.length === 0 ||
    // Table should show even with a single entry (including empty values).
    (viz !== "table" && usableCount < minN);

  function renderVizSection() {
    if (viz === "pie") {
      return pieConfig.labels.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {t("data_event_analysis_pie_no_checkbox_data")}
        </p>
      ) : (
        <div className="mx-auto h-56 w-full max-w-[13rem] sm:max-w-[15rem] sm:h-60">
          <Pie
            data={pieConfig}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: {
                  position: "bottom",
                  labels: { boxWidth: 10, font: { size: 11 } },
                },
              },
            }}
          />
        </div>
      );
    }
    if (viz === "timeseries") {
      return lineConfig.labels.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {t("data_event_analysis_timeseries_no_numeric")}
        </p>
      ) : (
        <div className="max-w-xl">
          <Line
            data={lineConfig}
            options={{
              responsive: true,
              maintainAspectRatio: true,
              scales: {
                x: {
                  ticks: {
                    autoSkip: true,
                    maxTicksLimit: 8,
                    maxRotation: 0,
                    minRotation: 0,
                    callback(tickValue) {
                      const i = parseInt(String(tickValue), 10);
                      if (!Number.isFinite(i)) return String(tickValue);
                      return formatTimeseriesAxisTick(lineConfig.meta, i);
                    },
                  },
                  grid: { display: false },
                },
                y:
                  timeseriesUseFieldScale && timeseriesAxisBounds != null
                    ? {
                        min: timeseriesAxisBounds.min,
                        max: timeseriesAxisBounds.max,
                        beginAtZero: timeseriesAxisBounds.min >= 0,
                      }
                    : timeseriesUseFieldScale
                      ? { beginAtZero: true }
                      : { beginAtZero: false },
              },
              plugins: {
                legend: { display: false },
                tooltip: {
                  callbacks: {
                    title: (items) => {
                      const idx = items?.[0]?.dataIndex;
                      if (
                        typeof idx === "number" &&
                        Array.isArray(lineConfig.meta) &&
                        lineConfig.meta[idx]
                      ) {
                        return formatWhen(lineConfig.meta[idx].t);
                      }
                      return items?.[0]?.label || "";
                    },
                  },
                },
              },
            }}
          />
        </div>
      );
    }
    if (viz === "ai") {
      if (!aiAllowed) {
        return (
          <p className="text-sm text-muted-foreground">
            {t("data_event_analysis_ai_text_only")}
          </p>
        );
      }
      return aiLoading ? (
        <GeneratingLoader context="prompt" className="py-2" />
      ) : aiError ? (
        <p className="text-sm text-destructive">{aiError}</p>
      ) : (
        <div className="prose prose-sm dark:prose-invert max-w-none">
          <ReactMarkdown>
            {normalizeMarkdownText(
              aiSummary || t("data_event_analysis_ai_empty"),
            )}
          </ReactMarkdown>
        </div>
      );
    }
    return (
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t("data_event_analysis_col_time")}</TableHead>
            <TableHead>{t("data_event_analysis_col_value")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {[...sortedEvents].reverse().map((ev, i) => {
            const v = ev.eventData?.[fieldKey];
            const display = formatDataEventCellDisplay(
              fieldKey,
              v,
              componentIndex,
            );
            return (
              <TableRow key={ev._id || i}>
                <TableCell className="whitespace-nowrap">
                  {formatWhen(ev.created)}
                </TableCell>
                <TableCell className="break-all">{display}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    );
  }

  return (
    <div className="space-y-3 pb-8 border-b border-border last:border-b-0 last:pb-0">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between sm:gap-4">
        <h3 className="text-sm font-medium">{displayTitle}</h3>
        {showVizType ? (
          <span className="text-xs font-medium text-muted-foreground">
            {t(`data_event_analysis_viz_${viz}`)}
          </span>
        ) : null}
      </div>
      {showEmpty ? (
        <p className="text-sm text-muted-foreground">
          {t("data_event_analysis_keep_logging")}
        </p>
      ) : (
        renderVizSection()
      )}
    </div>
  );
}

const DataEventAnalysis = ({
  processId,
  activity,
  content,
  activityComponents = [],
  refreshNonce = 0,
}) => {
  const { t } = useTranslation();
  const retryFetch = useRetryFetch();
  const c = normalizeContent(content);

  const componentIndex = useMemo(
    () => buildDataEventComponentIndex(activityComponents),
    [activityComponents],
  );

  const [events, setEvents] = useState([]);
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(false);
  const hasEvents = Array.isArray(events) && events.length > 0;

  const sortedEvents = useMemo(() => {
    const list = Array.isArray(events) ? [...events] : [];
    list.sort((a, b) => {
      const ta = new Date(a.created).getTime();
      const tb = new Date(b.created).getTime();
      return ta - tb;
    });
    return list;
  }, [events]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      const activityToQuery =
        typeof c.sourceActivityId === "string" && c.sourceActivityId.trim()
          ? c.sourceActivityId.trim()
          : activity;
      if (!processId || !activityToQuery) {
        setEvents([]);
        return;
      }
      // Avoid briefly showing stale charts (e.g., pie) while refetching.
      // Clear old events so UI shows loader until fresh data arrives.
      setEvents([]);
      setLoading(true);
      setLoadError(null);
      try {
        const q = new URLSearchParams({
          processId,
          activity: activityToQuery,
        });
        if (
          typeof c.sourceDataEventKey === "string" &&
          c.sourceDataEventKey.trim()
        ) {
          q.set("dataEventKey", c.sourceDataEventKey.trim());
        }
        const res = await retryFetch(
          `${config.API_BASE}/api/processDataEvent/search?${q.toString()}`,
        );
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        const data = await res.json();
        if (!cancelled) {
          setEvents(Array.isArray(data.list) ? data.list : []);
        }
      } catch (e) {
        if (!cancelled) {
          setLoadError(e.message || "error");
          setEvents([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- useRetryFetch returns a new function each render; including it causes infinite refetch
  }, [processId, activity, refreshNonce, c.sourceActivityId, c.sourceDataEventKey]);

  if (!processId) {
    return (
      <div className="mt-4 text-sm text-muted-foreground">
        {t("data_event_analysis_preview_no_process")}
      </div>
    );
  }

  if (
    !c.sourceActivityId ||
    !c.sourceDataEventKey ||
    !String(c.sourceActivityId).trim() ||
    !String(c.sourceDataEventKey).trim()
  ) {
    return (
      <div className="mt-4 text-sm text-muted-foreground">
        {t("data_event_analysis_select_source_data_event")}
      </div>
    );
  }

  if (c.analysisItems.length === 0) {
    return (
      <div className="mt-4 text-sm text-muted-foreground">
        {t("data_event_analysis_no_field_configured")}
      </div>
    );
  }

  return (
    <div className="mt-4 text-start space-y-3">
      {c.title ? (
        <h3 className="text-lg font-medium">{c.title}</h3>
      ) : null}
      {loading && !hasEvents ? (
        <GeneratingLoader context="synthesis" className="py-2" />
      ) : loadError ? (
        <p className="text-sm text-destructive">{loadError}</p>
      ) : (
        <div className="space-y-2">
          {loading ? (
            <div className="text-xs text-muted-foreground">
              Updating…
            </div>
          ) : null}
          {c.analysisItems.map((row, i) => (
            <DataEventAnalysisItemBlock
              key={row.id}
              fieldKey={row.fieldKey}
              title={row.title}
              viz={row.viz}
              colorIndex={i}
              sortedEvents={sortedEvents}
              minN={defaultMinSamplesForType(row.viz, c.minSamples)}
              processId={processId}
              activity={activity}
              t={t}
              retryFetch={retryFetch}
              componentIndex={componentIndex}
              showVizType={c.showVizType}
              timeseriesUseFieldScale={Boolean(row.timeseriesUseFieldScale)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default DataEventAnalysis;
